import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import useDebouncedEffect from '@/shared/hooks/useDebouncedEffect';
import { compareQueries, normalizeQuery } from 'packages/block-explorer/helpers/queryFilters';
import { Layout } from '../../components/Layout';
import AllSwapsTable from '../../components/SwapsPage/AllSwapsTable';
import { getFiltersFromUrl, toQueryObject } from '../../helpers/allSwaps';
import { useAllSwapsFilterStore } from '../../hooks/useFilterStores';
import { useQueryFromAllSwapsFilters } from '../../hooks/useQueryFromAllSwapsFilters';

export default function AllSwaps() {
  const router = useRouter();
  const { setFilters } = useAllSwapsFilterStore();

  const queryFromFiltersStore = useQueryFromAllSwapsFilters();

  const filterStateRef = useRef({
    hydrated: false,
    userInteracted: false,
    initialQuery: {} as Record<string, string>,
  });

  useEffect(() => {
    if (!router.isReady || filterStateRef.current.hydrated) return;

    const filtersFromUrl = getFiltersFromUrl(router.query);

    setFilters(filtersFromUrl);

    filterStateRef.current.initialQuery = toQueryObject(filtersFromUrl);
    filterStateRef.current.hydrated = true;
  }, [router.isReady]);

  useDebouncedEffect(
    () => {
      if (!filterStateRef.current.hydrated) return;

      const isSameAsInitial = compareQueries(
        queryFromFiltersStore,
        filterStateRef.current.initialQuery,
      );
      if (!isSameAsInitial) filterStateRef.current.userInteracted = true;

      const isSameAsRouter = compareQueries(normalizeQuery(router.query), queryFromFiltersStore);

      const isUrlSyncedWithFilters =
        (!filterStateRef.current.userInteracted && isSameAsInitial) || isSameAsRouter;

      if (isUrlSyncedWithFilters) return;

      router.replace(
        {
          pathname: router.pathname,
          query: queryFromFiltersStore,
        },
        undefined,
        { shallow: true },
      );
    },
    [queryFromFiltersStore],
    500,
  );

  return (
    <Layout title="All Swaps">
      <AllSwapsTable />
    </Layout>
  );
}
